import { useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { clsx } from '../clsx';
import { useFieldIds } from '../hooks';
import { Button } from './Button';
import { Progress } from './Progress';
import styles from './FileField.module.css';

export interface FileFieldItem {
  id: string;
  name: string;
  /** Formatted size, e.g. "18,2 MB". */
  size?: string | undefined;
  /** 0–100 while uploading; omitted or 100 = done. */
  progress?: number | undefined;
  /** Error text; the row turns to `--danger-bg` and offers "Erneut versuchen". */
  error?: string | undefined;
  /** Short type label in the icon ("pdf", "jpg"); derived from the name when omitted. */
  kind?: string | undefined;
}

export interface FileFieldProps {
  label: string;
  labelHidden?: boolean | undefined;
  /** Headline inside the drop area (`large`), e.g. "Alle Unterlagen auf einmal hierher ziehen". */
  title?: string | undefined;
  /** Second line, e.g. "PDF, Word, Excel, Bilder oder ein ZIP-Archiv." */
  description?: string | undefined;
  /** Button label; default "Dateien auswählen". */
  buttonLabel?: string | undefined;
  /** `large` is the accent dashed area of Objekt anlegen 02; `slot` the 104 px sunken frame of the settings (logo, favicon). */
  variant?: 'large' | 'slot' | undefined;
  accept?: string | undefined;
  multiple?: boolean | undefined;
  disabled?: boolean | undefined;
  onFiles: (files: File[]) => void;
  /** Uploaded, running and failed files, rendered as rows under the area. */
  items?: ReadonlyArray<FileFieldItem> | undefined;
  onRetry?: ((item: FileFieldItem) => void) | undefined;
  onRemove?: ((item: FileFieldItem) => void) | undefined;
  /** Summary line above the rows ("12 hochgeladen · 3 laufen · 1 Fehler"). */
  summary?: ReactNode | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  id?: string | undefined;
  name?: string | undefined;
  className?: string | undefined;
}

function kindOf(item: FileFieldItem): string {
  if (item.kind) return item.kind;
  const dot = item.name.lastIndexOf('.');
  return dot >= 0 ? item.name.slice(dot + 1).slice(0, 4) : 'file';
}

/** Drag & drop upload area with file rows (progress, error, retry). */
export function FileField({
  label,
  labelHidden = false,
  title,
  description,
  buttonLabel = 'Dateien auswählen',
  variant = 'large',
  accept,
  multiple = true,
  disabled = false,
  onFiles,
  items,
  onRetry,
  onRemove,
  summary,
  hint,
  error,
  id,
  name,
  className,
}: FileFieldProps) {
  const ids = useFieldIds(id, { hint: Boolean(hint), error: Boolean(error) });
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function emit(list: FileList | null) {
    if (!list || list.length === 0) return;
    onFiles(multiple ? Array.from(list) : Array.from(list).slice(0, 1));
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setOver(false);
    if (disabled) return;
    emit(event.dataTransfer.files);
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    emit(event.target.files);
    event.target.value = '';
  }

  return (
    <div className={clsx(styles.wrapper, disabled && styles.disabled, className)}>
      <span className={clsx(styles.label, labelHidden && styles.srOnly)} id={`${ids.inputId}-label`}>
        {label}
      </span>
      <div
        className={clsx(styles.area, variant === 'slot' ? styles.slot : styles.large, over && styles.over)}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          id={ids.inputId}
          name={name}
          type="file"
          className={styles.srOnly}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          aria-labelledby={`${ids.inputId}-label`}
          aria-describedby={ids.describedBy}
          aria-invalid={error ? true : undefined}
          onChange={onChange}
        />
        {variant === 'slot' ? (
          <button
            type="button"
            className={styles.slotButton}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {title ?? 'Noch nicht hinterlegt'}
          </button>
        ) : (
          <>
            <div className={styles.text}>
              {title ? <div className={styles.title}>{title}</div> : null}
              {description ? <div className={styles.description}>{description}</div> : null}
            </div>
            <Button variant="secondary" disabled={disabled} onClick={() => inputRef.current?.click()}>
              {buttonLabel}
            </Button>
          </>
        )}
      </div>
      {error ? (
        <div id={ids.errorId} className={styles.error} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div id={ids.hintId} className={styles.hint}>
          {hint}
        </div>
      ) : null}
      {items && items.length > 0 ? (
        <div className={styles.list}>
          {summary ? <div className={styles.summary}>{summary}</div> : null}
          {items.map((item) => {
            const running = item.progress !== undefined && item.progress < 100 && !item.error;
            return (
              <div key={item.id} className={clsx(styles.row, item.error && styles.rowError)}>
                <span className={styles.fileIcon}>{kindOf(item)}</span>
                <span className={styles.fileText}>
                  <span className={styles.fileName}>{item.name}</span>
                  {item.size ? <span className={styles.fileSize}>{item.size}</span> : null}
                </span>
                {item.error ? (
                  <span className={styles.rowActions}>
                    <span className={styles.errorText}>{item.error}</span>
                    {onRetry ? (
                      <Button variant="secondary" size="small" onClick={() => onRetry(item)}>
                        Erneut versuchen
                      </Button>
                    ) : null}
                  </span>
                ) : running ? (
                  <span className={styles.progress}>
                    <Progress value={item.progress ?? 0} label={item.name} variant="thin" showValue />
                  </span>
                ) : onRemove ? (
                  <Button variant="secondary" size="small" onClick={() => onRemove(item)}>
                    Entfernen
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
